import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { SubscriptionPlan, SubscriptionStatus } from '@vi-iot/client';

import { BookingActions } from './booking.actions';
import { BookingState } from './booking.state';
import { CANCELING_STATUS, DEFAULT_BOOKING_PAGE_SIZE } from './booking.model';

export const initBookingState: BookingState = {
  bookingDevices: {
    error: null,
    loading: false,
    value: null,
  },
  bookingDevicesSelected: [],
  allDevicesCount: 0,
  plan: null,
  subscriptionsPlans: {
    value: [],
    loaded: false,
    loading: false,
    error: null,
  },
  subscriptions: {
    value: [],
    loaded: false,
    loading: false,
    error: null,
  },
  subscriptionsDetails: {
    value: null,
    loaded: false,
    loading: false,
    error: null,
  },
  trialSubscriptions: {
    value: null,
    loaded: false,
    loading: false,
    error: null,
  },
  voucherSubscriptions: {
    loaded: false,
    loading: false,
    error: null,
  },
  voucherValidation: {
    reasons: null,
    valid: null,
    loaded: false,
    loading: false,
    error: null,
  },
  cancelSubscriptionStatus: CANCELING_STATUS.START,
  contingentSubscriptions: null,
  loading: false,
  loaded: false,
  error: null,
  simulateSubscription: {
    error: null,
    loaded: false,
    loading: false,
  },
  buySubscriptionError: null,
  pagination: {
    page: 0,
    pageSize: DEFAULT_BOOKING_PAGE_SIZE,
  },
  searchInput: null,
  bookingTrialStatus: null,
};

export const bookingReducer: ActionReducer<BookingState, Action> = createReducer(
  initBookingState,

  on(
    BookingActions.setLoadingIndicator,
    (state): BookingState => ({
      ...state,
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.subscribePackage,
    (state): BookingState => ({
      ...state,
      buySubscriptionError: null,
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.subscribePackageSuccess,
    (state): BookingState => ({
      ...state,
      buySubscriptionError: null,
      loading: false,
      loaded: true,
    })
  ),

  on(
    BookingActions.subscribePackageError,
    (state, { error }): BookingState => ({
      ...state,
      buySubscriptionError: error,
      loading: false,
      loaded: true,
    })
  ),

  on(
    BookingActions.resetUserSelection,
    (state): BookingState => ({
      ...state,
      bookingDevicesSelected: [],
    })
  ),

  on(
    BookingActions.loadBookingInstallations.attempt,
    (state): BookingState => {
      return {
        ...state,
        bookingDevices: {
          value: null,
          loading: true,
          error: null,
        },
      };
    }
  ),

  on(
    BookingActions.loadBookingInstallations.success,
    (state, { bookingDevices, allDevicesCount, preselectedSerials }): BookingState => {
      const bookingDevicesSelected = [];

      if (Array.isArray(preselectedSerials)) {
        preselectedSerials.forEach((serial) => {
          const selectedDevice = bookingDevices.find(
            (bookingDevice) => bookingDevice.vin === serial
          );

          bookingDevicesSelected.push(selectedDevice);
        });
      }

      return {
        ...state,
        bookingDevices: {
          ...state.bookingDevices,
          value: bookingDevices,
          loading: false,
        },
        bookingDevicesSelected,
        allDevicesCount,
        loading: false,
        loaded: true,
        error: null,
      };
    }
  ),

  on(
    BookingActions.loadBookingInstallations.error,
    (state, { error }): BookingState => {
      return {
        ...state,
        bookingDevices: {
          value: null,
          loading: false,
          error,
        },
      };
    }
  ),

  on(
    BookingActions.toggleInstallationSelection,
    (state, { deviceSerial }): BookingState => {
      const selected = state.bookingDevicesSelected;
      const device = state.bookingDevices.value.find(({ vin }) => vin === deviceSerial);

      return {
        ...state,
        bookingDevicesSelected: selected.find((installation) => installation.vin === deviceSerial)
          ? selected.filter(({ vin }) => vin !== deviceSerial)
          : [...selected, device],
      };
    }
  ),

  on(
    BookingActions.selectInstallations,
    (state, { deviceSerials }): BookingState => ({
      ...state,
      bookingDevicesSelected: [
        ...state.bookingDevicesSelected,
        ...deviceSerials.map((serial) =>
          state.bookingDevices.value.find((installation) => installation.vin === serial)
        ),
      ],
    })
  ),

  on(
    BookingActions.deselectInstallations,
    (state, { deviceSerials }): BookingState => ({
      ...state,
      bookingDevicesSelected: state.bookingDevicesSelected.filter(({ vin }) =>
        deviceSerials.includes(vin)
      ),
    })
  ),

  on(
    BookingActions.selectBookingPlan,
    (state, { plan }): BookingState => ({
      ...state,
      plan,
    })
  ),

  on(
    BookingActions.fetchSubscriptionsPlans,
    (state): BookingState => ({
      ...state,
      subscriptionsPlans: {
        ...state.subscriptionsPlans,
        loaded: false,
        loading: true,
        error: null,
      },
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.fetchSubscriptionsPlansSuccess,
    (state, { subscriptionsPlans }): BookingState => ({
      ...state,
      subscriptionsPlans: {
        value: subscriptionsPlans,
        loaded: true,
        loading: false,
        error: null,
      },
      loading: false,
      loaded: true,
    })
  ),

  on(
    BookingActions.fetchSubscriptionsPlansError,
    (state, { error }): BookingState => ({
      ...state,
      subscriptionsPlans: {
        ...state.subscriptionsPlans,
        loaded: false,
        loading: false,
        error,
      },
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    BookingActions.getVitoguideSubscriptions,
    (state): BookingState => ({
      ...state,
      subscriptions: {
        ...state.subscriptions,
        loaded: false,
        loading: true,
        error: null,
      },
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.getVitoguideSubscriptionsSuccess,
    (state, { subscriptions }): BookingState => ({
      ...state,
      subscriptions: {
        value: subscriptions,
        loaded: true,
        loading: false,
        error: null,
      },
      loading: false,
      loaded: true,
      error: null,
    })
  ),

  on(
    BookingActions.getVitoguideSubscriptionsError,
    (state, { error, isResetSubcriptions }): BookingState => ({
      ...state,
      subscriptions: {
        value: isResetSubcriptions ? [] : state.subscriptions.value,
        loaded: true,
        loading: false,
        error,
      },
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    BookingActions.updateVitoguideSubscription,
    (state): BookingState => ({
      ...state,
      loading: true,
      loaded: false,
      error: null,
      buySubscriptionError: null,
    })
  ),

  on(
    BookingActions.updateVitoguideSubscriptionSuccess,
    (state, { boilerSerial }): BookingState => ({
      ...state,
      subscriptions: {
        ...state.subscriptions,
        value: state.subscriptions.value.map((subscription) =>
          subscription.boilerId === boilerSerial
            ? {
                ...subscription,
                status: SubscriptionStatus.active,
                plan: SubscriptionPlan.Pro,
              }
            : subscription
        ),
      },
      loading: false,
      loaded: true,
      buySubscriptionError: null,
    })
  ),

  on(
    BookingActions.updateVitoguideSubscriptionError,
    (state, { error }): BookingState => ({
      ...state,
      loading: false,
      loaded: true,
      buySubscriptionError: error,
    })
  ),

  on(
    BookingActions.cancelVitoguideSubscriptionsSuccess,
    (state, { boilerSerial }): BookingState => {
      return {
        ...state,
        cancelSubscriptionStatus: CANCELING_STATUS.SUCCESS,
        subscriptions: {
          ...state.subscriptions,
          value: state.subscriptions.value.map((subscription) =>
            subscription.boilerId === boilerSerial
              ? {
                  ...subscription,
                  status: SubscriptionStatus.canceled,
                }
              : subscription
          ),
        },
      };
    }
  ),

  on(
    BookingActions.flatRateCancelSubscriptionSuccess,
    (state): BookingState => ({
      ...state,
      cancelSubscriptionStatus: CANCELING_STATUS.SUCCESS,
      subscriptions: {
        ...state.subscriptions,
        value: state.subscriptions.value.map((subscription) =>
          subscription.status === SubscriptionStatus.active
            ? { ...subscription, status: SubscriptionStatus.canceled }
            : subscription
        ),
      },
    })
  ),

  on(
    BookingActions.flatRateCancelSubscriptionError,
    (state): BookingState => ({
      ...state,
      cancelSubscriptionStatus: CANCELING_STATUS.FAILED,
    })
  ),

  on(
    BookingActions.cancelVitoguideSubscriptionsError,
    (state, { error }): BookingState => ({
      ...state,
      cancelSubscriptionStatus: CANCELING_STATUS.FAILED,
      error,
    })
  ),

  on(
    BookingActions.resetCancelSubscriptionStatus,
    (state): BookingState => ({
      ...state,
      cancelSubscriptionStatus: CANCELING_STATUS.START,
    })
  ),

  on(
    BookingActions.createTrialSubscription,
    (state): BookingState => ({
      ...state,
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.createTrialSubscriptionSuccess,
    (state, { bookingTrialStatus, bookingSubscriptions }): BookingState => {
      return {
        ...state,
        loading: false,
        loaded: true,
        error: null,
        bookingTrialStatus,
        subscriptions: {
          ...state.subscriptions,
          value: [...state.subscriptions.value, ...bookingSubscriptions],
        },
      };
    }
  ),

  on(
    BookingActions.createTrialSubscriptionError,
    (state, { error, bookingTrialStatus }): BookingState => ({
      ...state,
      loading: false,
      loaded: true,
      error,
      bookingTrialStatus,
    })
  ),

  on(
    BookingActions.getTrialSubscriptions,
    (state): BookingState => ({
      ...state,
      trialSubscriptions: {
        ...state.trialSubscriptions,
        loaded: false,
        loading: true,
        error: null,
      },
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.getTrialSubscriptionsSuccess,
    (state, { trialSubscriptions }): BookingState => ({
      ...state,
      trialSubscriptions: {
        value: trialSubscriptions,
        loaded: true,
        loading: false,
        error: null,
      },
      loading: false,
      loaded: true,
    })
  ),

  on(
    BookingActions.getTrialSubscriptionsError,
    (state, { error }): BookingState => ({
      ...state,
      trialSubscriptions: {
        ...state.trialSubscriptions,
        loaded: true,
        loading: false,
        error,
      },
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    BookingActions.subscribeVoucherPackage,
    (state): BookingState => ({
      ...state,
      voucherSubscriptions: {
        ...state.voucherSubscriptions,
        loaded: false,
        loading: true,
        error: null,
      },
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.subscribeVoucherPackageSuccess,
    (state): BookingState => ({
      ...state,
      voucherSubscriptions: {
        loaded: true,
        loading: false,
        error: null,
      },
      loading: false,
      loaded: true,
    })
  ),

  on(
    BookingActions.subscribeVoucherPackageError,
    (state, { error }): BookingState => ({
      ...state,
      voucherSubscriptions: {
        ...state.voucherSubscriptions,
        loaded: true,
        loading: false,
        error,
      },
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    BookingActions.validateVoucherPackage,
    (state): BookingState => ({
      ...state,
      voucherValidation: {
        ...state.voucherValidation,
        loaded: false,
      },
    })
  ),

  on(
    BookingActions.validateVoucherPackageSuccess,
    (state, { voucherValidationDetails }): BookingState => ({
      ...state,
      voucherValidation: {
        reasons: voucherValidationDetails.reasons,
        valid: voucherValidationDetails.valid,
        loaded: true,
        loading: false,
        error: null,
      },
    })
  ),

  on(
    BookingActions.validateVoucherPackageError,
    (state, { error }): BookingState => ({
      ...state,
      voucherValidation: {
        ...state.voucherValidation,
        valid: false,
        loaded: true,
        error,
      },
    })
  ),

  on(
    BookingActions.getContingentSubscriptions,
    (state): BookingState => ({
      ...state,
      loading: true,
      loaded: false,
    })
  ),

  on(
    BookingActions.getContingentSubscriptionsSuccess,
    (state, { contingentSubscriptions }): BookingState => ({
      ...state,
      contingentSubscriptions,
      loading: false,
      loaded: true,
    })
  ),

  on(
    BookingActions.getContingentSubscriptionsError,
    (state, { error }): BookingState => ({
      ...state,
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    BookingActions.getSubscriptionsDetails,
    (state): BookingState => ({
      ...state,
      subscriptionsDetails: {
        ...state.subscriptionsDetails,
        loaded: false,
        loading: true,
        error: null,
      },
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.getSubscriptionsDetailsSuccess,
    (state, { subscriptionsDetails }): BookingState => ({
      ...state,
      subscriptionsDetails: {
        value: subscriptionsDetails,
        loaded: true,
        loading: false,
        error: null,
      },
      loading: false,
      loaded: true,
      error: null,
    })
  ),

  on(
    BookingActions.getSubscriptionsDetailsError,
    (state, { error }): BookingState => ({
      ...state,
      subscriptionsDetails: {
        ...state.subscriptionsDetails,
        loaded: true,
        loading: false,
        error,
      },
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    BookingActions.resetSubscriptionsDetails,
    (state): BookingState => ({
      ...state,
      subscriptionsDetails: {
        value: null,
        loaded: false,
        loading: false,
        error: null,
      },
      voucherValidation: {
        reasons: null,
        valid: null,
        loaded: false,
        loading: false,
        error: null,
      },
    })
  ),

  on(
    BookingActions.simulateSubscription,
    (state): BookingState => ({
      ...state,
      simulateSubscription: {
        loading: true,
        loaded: false,
        error: null,
      },
    })
  ),

  on(
    BookingActions.simulateSubscriptionSuccess,
    (state): BookingState => ({
      ...state,
      simulateSubscription: {
        loading: false,
        loaded: true,
        error: null,
      },
    })
  ),

  on(
    BookingActions.simulateSubscriptionError,
    (state, { error }): BookingState => ({
      ...state,
      simulateSubscription: {
        loading: false,
        loaded: true,
        error,
      },
    })
  ),

  on(
    BookingActions.flatRateGetPackagePriceDetails,
    (state): BookingState => ({
      ...state,
      subscriptionsDetails: {
        ...state.subscriptionsDetails,
        loaded: false,
      },
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.commercialGetPackagePriceDetails,
    (state): BookingState => ({
      ...state,
      subscriptionsDetails: {
        ...state.subscriptionsDetails,
        loaded: false,
      },
      loading: true,
      loaded: false,
      error: null,
    })
  ),

  on(
    BookingActions.setBookingPaginationPage,
    (state, { page }): BookingState => {
      return {
        ...state,
        pagination: {
          ...state.pagination,
          page,
        },
      };
    }
  ),

  on(
    BookingActions.setSearchInputValue,
    (state, { searchInput }): BookingState => {
      return {
        ...state,
        searchInput,
      };
    }
  ),

  on(
    BookingActions.clearBookingState,
    (state): BookingState => {
      return {
        ...state,
        bookingDevices: {
          value: null,
          loading: false,
          error: null,
        },
      };
    }
  )
);
